import { BALANCE, BUILDING_DEFS } from '../constants';
import type { Simulation } from '../sim';
import type { LedgerKind, PayrollBreakdown, SimState } from '../types';
import { GOOD_KINDS } from '../types';
// Circular with endgame.ts (which uses pushLedger) — safe: both sides only
// reference each other inside function bodies, never at module-eval time.
import { recordEnding } from './endgame';
import { tierWageCents } from './tiers';
import { collectTownIncomeWeekly, rollIncomeHistories } from './town';
import { functionalCapacity } from './workforce';

export function pushLedger(state: SimState, day: number, kind: LedgerKind, amountCents: number, note: string): void {
  state.economy.ledger.push({ day, kind, amountCents, note });
  if (state.economy.ledger.length > BALANCE.ledgerCap) state.economy.ledger.shift();
}

function cargoValueCents(state: SimState, ironDt: number, goldDt: number): number {
  const market = state.economy.market;
  let value = Math.floor((ironDt * market.iron.price + goldDt * market.gold.price) / 10);
  // A working rail depot gets better prices than trucking it out.
  if (state.buildings.some((building) => building.type === 'railDepot' && building.functional)) {
    value = Math.floor(value * (1 + BALANCE.railOreBonus));
  }
  return value;
}

export function dispatchTruck(sim: Simulation, manual: boolean): boolean {
  const state = sim.state;
  const truck = state.economy.truck;
  const depot = state.economy.depot;
  const total = depot.ironDt + depot.goldDt;
  if (truck.state !== 'idle' || state.meta.gameOver) return false;
  if (manual ? total < BALANCE.truckMinManualDt : total < BALANCE.truckAutoDepartDt) return false;
  const value = cargoValueCents(state, depot.ironDt, depot.goldDt) - BALANCE.truckFeeCents;
  truck.state = 'selling';
  truck.returnsOnDay = sim.day + BALANCE.truckSettleDays;
  truck.cargoValueCents = value;
  const note = `${(depot.ironDt / 10).toFixed(1)}t iron, ${(depot.goldDt / 10).toFixed(1)}t gold`;
  const quota = state.letters.quota;
  if (quota && !quota.completed && !quota.failed) {
    quota.deliveredDt += quota.oreType === 1 ? depot.ironDt : depot.goldDt;
    if (quota.deliveredDt >= quota.amountDt) {
      quota.completed = true;
      state.town.quotasCompleted += 1;
      state.economy.cashCents += quota.rewardCents;
      pushLedger(state, sim.day, 'reward', quota.rewardCents, 'HQ quota bonus');
      sim.pushEvent({ kind: 'quotaCompleted' });
      sim.pushEvent({ kind: 'toast', message: 'Quota met! HQ wired the bonus.' });
    }
  }
  depot.ironDt = 0;
  depot.goldDt = 0;
  sim.pushEvent({ kind: 'truckDeparted', cargoValueCents: value });
  sim.pushEvent({ kind: 'toast', message: `Ore truck departed (${note})` });
  return true;
}

/** Market value of everything in the goods depot, rail bonus included. */
export function goodsCargoValueCents(state: SimState): number {
  const market = state.economy.market;
  let value = 0;
  for (const good of GOOD_KINDS) {
    value += state.economy.goodsDepot.units[good] * market[good].price;
  }
  if (state.buildings.some((building) => building.type === 'railDepot' && building.functional)) {
    value = Math.floor(value * (1 + BALANCE.goodsRailBonus));
  }
  return value;
}

/** The goods freight run. Needs a working freight office to book the wagon;
 * auto-departs on cargo VALUE, not tonnage — chips ship sooner than corn. */
export function dispatchGoodsTruck(sim: Simulation, manual: boolean): boolean {
  const state = sim.state;
  const truck = state.economy.goodsTruck;
  if (truck.state !== 'idle' || state.meta.gameOver) return false;
  if (!state.buildings.some((building) => building.type === 'freightOffice' && building.functional)) return false;
  const gross = goodsCargoValueCents(state);
  // Never book a run that loses money on the fee.
  if (gross <= BALANCE.goodsTruckFeeCents) return false;
  if (!manual && gross < BALANCE.goodsTruckAutoDepartValueCents) return false;
  const value = gross - BALANCE.goodsTruckFeeCents;
  truck.state = 'selling';
  truck.returnsOnDay = sim.day + BALANCE.goodsTruckSettleDays;
  truck.cargoValueCents = value;
  const parts: string[] = [];
  for (const good of GOOD_KINDS) {
    const units = state.economy.goodsDepot.units[good];
    if (units > 0) parts.push(`${units} ${good}`);
    state.economy.goodsDepot.units[good] = 0;
  }
  sim.pushEvent({ kind: 'goodsTruckDeparted', cargoValueCents: value });
  sim.pushEvent({ kind: 'toast', message: `Freight wagon departed (${parts.join(', ')})` });
  return true;
}

/** Daily: truck settlements + auto-departures, ore and goods alike. */
export function stepEconomyDaily(sim: Simulation): void {
  const state = sim.state;
  const truck = state.economy.truck;
  if (truck.state === 'selling' && sim.day >= truck.returnsOnDay) {
    state.economy.cashCents += truck.cargoValueCents;
    state.town.weekMineIncomeCents += Math.max(0, truck.cargoValueCents);
    pushLedger(state, sim.day, 'sale', truck.cargoValueCents, 'Ore sale');
    sim.pushEvent({ kind: 'sale', amountCents: truck.cargoValueCents, note: 'Ore sale settled' });
    if (!sim.firstSaleDone) {
      sim.firstSaleDone = true;
    }
    truck.state = 'idle';
    truck.cargoValueCents = 0;
  }
  const goodsTruck = state.economy.goodsTruck;
  if (goodsTruck.state === 'selling' && sim.day >= goodsTruck.returnsOnDay) {
    state.economy.cashCents += goodsTruck.cargoValueCents;
    // Goods money is TOWN income, not mine income — the endgame's mineShare
    // test hinges on this line.
    state.town.weekGoodsIncomeCents += Math.max(0, goodsTruck.cargoValueCents);
    pushLedger(state, sim.day, 'sale', goodsTruck.cargoValueCents, 'Goods sale');
    sim.pushEvent({ kind: 'sale', amountCents: goodsTruck.cargoValueCents, note: 'Goods sale settled' });
    if (!sim.firstGoodsSaleDone) {
      sim.firstGoodsSaleDone = true;
    }
    goodsTruck.state = 'idle';
    goodsTruck.cargoValueCents = 0;
  }
  dispatchTruck(sim, false);
  dispatchGoodsTruck(sim, false);
}

/** The weekly settlement: wages out, recapture in, fuel, interest, bankruptcy. */
export function runPayroll(sim: Simulation): PayrollBreakdown {
  const state = sim.state;
  const day = sim.day;
  const workforce = state.workforce;

  const beds = functionalCapacity(state, 'beds');
  const foodCap = functionalCapacity(state, 'foodCap');
  const drinkCap = functionalCapacity(state, 'drinkCap');
  const gambleCap = functionalCapacity(state, 'gambleCap');

  // Wages: miners, haulers, and industry staff by tier — nobody else is on
  // the books. The industry split is reported so the ledger shows what the
  // town's own economy costs to run.
  let wages = 0;
  let industryWages = 0;
  let onPayroll = 0;
  for (const peep of state.peeps) {
    const wage = tierWageCents(peep);
    wages += wage;
    if (peep.job === 'industry') industryWages += wage;
    if (wage > 0) onPayroll += 1;
  }

  const workers = state.peeps.filter((peep) => peep.role === 'worker');
  const bedSleepers = workers.filter((peep) => peep.householdId === null).length;
  const adults = state.peeps.filter((peep) => peep.role !== 'child').length;

  // Rent: bunk beds from single workers, real rent from resident households.
  const bunkRent = Math.min(bedSleepers, beds) * BALANCE.rentCentsPerWeek;
  const buildingsById = new Map(state.buildings.map((building) => [building.id, building]));
  let householdRent = 0;
  for (const household of state.households) {
    const home = buildingsById.get(household.homeBuildingId);
    if (home) householdRent += BUILDING_DEFS[home.type].rentCentsPerWeek ?? 0;
  }
  const rent = bunkRent + householdRent;

  let food = foodCap > 0 ? Math.min(adults, foodCap) * BALANCE.foodSpendCentsPerWeek : 0;
  if (foodCap > 0) food += state.households.length * BALANCE.familyStoreSpendCents;
  // Anyone who can swing a pick can hold a glass: every adult drinks/gambles.
  const saloon = workforce.unlocked.drink
    ? Math.floor(Math.min(adults, drinkCap) * BALANCE.saloonSpendCentsPerWeek * BALANCE.saloonUptake)
    : 0;
  const casino = workforce.unlocked.gambling
    ? Math.floor(Math.min(adults, gambleCap) * BALANCE.casinoSpendCentsPerWeek * BALANCE.casinoUptake)
    : 0;

  let fuel = 0;
  for (const building of state.buildings) {
    fuel += BUILDING_DEFS[building.type].fuelCentsPerWeek ?? 0;
  }

  const interest = Math.floor(state.economy.debtCents * BALANCE.debtInterestPerWeek);
  state.economy.debtCents += interest;

  const net = rent + food + saloon + casino - wages - fuel;
  state.economy.cashCents += net;

  // Town-side income (taxes, commerce, rail, bank, retail, power, fares) once
  // incorporated, and the trailing histories the endgame watches. Goods-truck
  // settlements accumulated during the week roll in here too.
  const goodsSales = state.town.weekGoodsIncomeCents;
  const oreSales = state.town.weekMineIncomeCents;
  const townIncome = collectTownIncomeWeekly(sim);
  rollIncomeHistories(sim, townIncome.total + householdRent);

  if (wages > 0) pushLedger(state, day, 'payroll', -wages, `Wages (${onPayroll} on payroll)`);
  if (rent > 0) pushLedger(state, day, 'rent', rent, 'Company housing rent');
  if (food > 0) pushLedger(state, day, 'amenity', food, 'Canteen & store takings');
  if (saloon > 0) pushLedger(state, day, 'amenity', saloon, 'Saloon takings');
  if (casino > 0) pushLedger(state, day, 'amenity', casino, 'Casino takings');
  if (fuel > 0) pushLedger(state, day, 'fuel', -fuel, 'Generator fuel');
  if (interest > 0) pushLedger(state, day, 'interest', -interest, 'Debt interest accrued');

  const missed = state.economy.cashCents < 0;
  if (missed) {
    workforce.payrollMissedStreak += 1;
    workforce.morale = Math.max(0, workforce.morale - BALANCE.missedPayrollMoraleHit);
    workforce.paidOnTime = 0;
  } else {
    workforce.payrollMissedStreak = 0;
    workforce.paidOnTime = 1;
  }

  // Severance was deducted from cash at dismissal time; the settlement just
  // reports it, then the accumulator resets for the new week.
  const severance = state.economy.weekSeveranceCents;
  state.economy.weekSeveranceCents = 0;

  const breakdown: PayrollBreakdown = {
    wages,
    industryWages,
    rent,
    food,
    saloon,
    casino,
    fuel,
    interest,
    severance,
    town: townIncome.total,
    goodsSales,
    oreSales,
    powerSales: townIncome.power,
    passengerFares: townIncome.passengers,
    net: net + townIncome.total,
    missed,
  };
  sim.pushEvent({ kind: 'payroll', breakdown });
  checkBankruptcy(sim);
  return breakdown;
}

export function checkBankruptcy(sim: Simulation): void {
  const state = sim.state;
  if (state.economy.cashCents >= 0 || state.meta.gameOver) return;
  // Once HQ hands over the site there is no HQ to foreclose — insolvency is
  // judged by the ghost-town spiral in the endgame system instead.
  if (state.meta.era === 'installments' || state.meta.era === 'independent') return;
  if (state.economy.bailoutsUsed === 0) {
    state.economy.bailoutsUsed = 1;
    state.economy.cashCents += BALANCE.bailoutCents;
    state.economy.debtCents += BALANCE.bailoutCents;
    pushLedger(state, sim.day, 'bailout', BALANCE.bailoutCents, 'HQ emergency loan');
    sim.pushEvent({ kind: 'bankruptcy', final: false });
    sim.pushEvent({ kind: 'toast', message: 'HQ wired an emergency loan — with interest.' });
  } else {
    state.meta.gameOver = true;
    state.meta.gameOverReason = 'The company went bankrupt twice. HQ has foreclosed the site.';
    recordEnding(sim, 'foreclosed');
    sim.pushEvent({ kind: 'bankruptcy', final: true });
  }
}
